import type {
  Datenquelle, Abschluss, Importlaufkopf, Messwerte, Vorgangskopf } from './typen'
import type { Formelbefund } from './Formel'
import type { Kachel, Kachelbezug, Kachelraum, Sprung, Verlaufspunkt } from './Kacheln'
import { balken, istKachelbefund, werteKachel, zeigeZahl } from './Kacheln'
import { Zustandssymbol } from './Zustand'
import type { Zustandsart } from './Zustand'
import {
  abschlussstatus,
  aktuelleSprache,
  anzahlwort,
  betrag,
  bewertungsebenenname,
  gesellschaftsname,
  kreisname,
  periodenname,
  t,
} from './sprache'

/**
 * Was in einer Kachel steht (7.5 Abschnitt 3.6, R347).
 *
 * **Drei Arten, und jede trägt dieselben vier Dinge:** eine Zahl, ein Ziel,
 * ihre Herkunft und ihren Zustand. Die Herkunft steht im Rahmen
 * (`Kachelbrett.tsx`), die übrigen drei stehen hier.
 *
 * Das Zustandssymbol kommt aus `Zustand.tsx` und nicht aus einer eigenen
 * Zeichnung: Es ist derselbe Zustand wie in der Arbeitsliste und im Bericht,
 * und **Farbe trägt nie allein** (R143) — die Form unterscheidet auch im
 * Graustufendruck.
 */

/** Ein Befund als Satz — Ursache, Menge und Weg (R145). */
export function Kachelbefundzeile({ fehler }: { fehler: Formelbefund }) {
  return <p className="kachelbefund">{t(fehler.schluessel, fehler.werte)}</p>
}

/* --- Zahl mit Weg ---------------------------------------------------------- */

export function Zahlkachel({
  kachel,
  raum,
  aufSprung,
  aufNachweis,
}: {
  kachel: Kachel
  raum: Kachelraum
  aufSprung: (ziel: Sprung) => void
  aufNachweis: (bezug: Kachelbezug, raum: Kachelraum) => void
}) {
  const wert = werteKachel(kachel, raum)

  if (istKachelbefund(wert)) {
    return <Kachelbefundzeile fehler={wert.fehler} />
  }

  return (
    <div className="kachelzahl">
      <button
        type="button"
        className={`zahlfeld ${wert.zustand}`}
        onClick={() => wert.ziel && aufSprung(wert.ziel)}
        disabled={!wert.ziel}
      >
        <Zustandssymbol art={wert.zustand} />
        <span className="zahl">{wert.text}</span>
      </button>

      {/*
        **Die Rechnung steht unter der Zahl, nicht hinter einem Menü** (R342).
        Eine Kennzahl, deren Herleitung man suchen muss, wird nicht geprüft —
        und eine ungeprüfte Kennzahl ist in einem Konsolidierungssystem
        schlimmer als keine.
      */}
      {wert.rechnung && (
        <details className="kachelrechnung">
          <summary>{t('kachel.formel.rechnung')}</summary>
          <p className="rechnung">{wert.rechnung}</p>
          <ul className="operanden">
            {wert.operanden.map((o) => (
              <li key={o.schluessel}>
                <button
                  type="button"
                  className="operand"
                  onClick={() => aufNachweis(o, raum)}
                  disabled={!o.nachweis}
                  title={t('kachel.nachweis')}
                >
                  <span className="name">{o.name}</span>
                  <span className="wert">{zeigeZahl(o.wert, o.einheit, o.waehrung)}</span>
                </button>
              </li>
            ))}
          </ul>
          <p className="anzeigehinweis">
            <strong>{t('kachel.formel.anzeige')}</strong> {t('kachel.formel.anzeige.erklaerung')}
          </p>
        </details>
      )}
    </div>
  )
}

/* --- Verlauf über die Zeitachse -------------------------------------------- */

export type Verlauf =
  | { art: 'laedt' }
  /** Der Abruf scheiterte technisch — das sagt das Störungsfenster (7.2 Abschnitt 30, R1003). */
  | { art: 'gestoert' }
  | { art: 'befund'; fehler: Formelbefund }
  | { art: 'punkte'; punkte: Verlaufspunkt[]; waehrung: string | null }

export function Verlaufkachel({
  kachel,
  verlauf,
  aufSprung,
  ziel,
}: {
  kachel: Kachel
  verlauf: Verlauf
  aufSprung: (ziel: Sprung) => void
  ziel: Sprung | null
}) {
  if (verlauf.art === 'laedt') {
    return <p className="hinweis">{t('kachel.laedt')}</p>
  }

  // Kein „kein Lauf", wo es nur eine Störung gab; die steht im Störungsfenster.
  if (verlauf.art === 'gestoert') {
    return null
  }

  if (verlauf.art === 'befund') {
    return <Kachelbefundzeile fehler={verlauf.fehler} />
  }

  const punkte = verlauf.punkte
  const erster = punkte[0]
  const letzter = punkte[punkte.length - 1]
  if (!erster || !letzter) {
    return <p className="hinweis">{t('kachel.verlauf.ohne')}</p>
  }

  const gezeichnet = balken(punkte)
  const zeige = (wert: number): string => zeigeZahl(wert, kachel.einheit, verlauf.waehrung)

  return (
    <div className="kachelverlauf">
      {/*
        **Der letzte Wert steht als Zahl daneben** und nicht nur als Balken
        (R347): Ein Verlauf, den man nur ablesen kann, indem man einen Balken
        schätzt, trägt keine Zahl — und eine Kachel ohne Zahl gibt es nicht.
      */}
      <p className="letzter">
        <span className="zahl">{zeige(letzter.wert)}</span>
        <span className="wann">{periodenname(letzter.periode)}</span>
      </p>

      <button
        type="button"
        className="zeichnung"
        onClick={() => ziel && aufSprung(ziel)}
        disabled={!ziel}
        aria-label={t('kachel.art.verlauf')}
      >
        {kachel.darstellung === 'balken' ? (
          <span className="balkenfeld">
            {gezeichnet.streifen.map((s) => (
              <span
                key={s.periode}
                className={`streifen ${s.wert < 0 ? 'negativ' : 'positiv'}`}
                style={{ top: `${s.von}%`, height: `${Math.max(s.hoehe, 0.8)}%` }}
                title={`${periodenname(s.periode)}: ${zeige(s.wert)}`}
              />
            ))}
            <span className="nulllinie" style={{ top: `${gezeichnet.nulllinie}%` }} />
          </span>
        ) : (
          <Linienzug punkte={punkte} beschriftung={(p) => `${periodenname(p.periode)}: ${zeige(p.wert)}`} />
        )}
      </button>

      <p className="achse">
        <span>{periodenname(erster.periode)}</span>
        <span>{periodenname(letzter.periode)}</span>
      </p>
    </div>
  )
}

/**
 * Der Linienzug — dieselben Zahlen, andere Form.
 *
 * Bewusst als `<svg>` von Hand und nicht mit einer Zeichenbibliothek: Eine
 * Linie über zwölf Punkte ist ein `polyline`-Element, und eine Abhängigkeit
 * dafür wäre mehr Last als Gewinn (ADR 005, R158 — nichts wird nachgeladen).
 */
function Linienzug({
  punkte,
  beschriftung,
}: {
  punkte: readonly Verlaufspunkt[]
  beschriftung: (p: Verlaufspunkt) => string
}) {
  const werte = punkte.map((p) => p.wert)
  const hoch = Math.max(...werte)
  const tief = Math.min(...werte)
  const spanne = hoch - tief || 1
  const schritt = punkte.length > 1 ? 100 / (punkte.length - 1) : 0

  const stellen = punkte.map((p, i) => ({
    x: i * schritt,
    y: ((hoch - p.wert) / spanne) * 100,
    punkt: p,
  }))

  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="linienfeld" role="img">
      <polyline points={stellen.map((s) => `${s.x},${s.y}`).join(' ')} vectorEffect="non-scaling-stroke" />
      {stellen.map((s) => (
        <circle key={s.punkt.periode} cx={s.x} cy={s.y} r="1.6" vectorEffect="non-scaling-stroke">
          <title>{beschriftung(s.punkt)}</title>
        </circle>
      ))}
    </svg>
  )
}

/* --- Stand und Fortschritt -------------------------------------------------- */

export type Standzeile = {
  id: string
  name: string
  menge: string
  zustand: Zustandsart
  ziel: Sprung | null
}

export function Standkachel({
  zeilen,
  kopf,
  aufSprung,
}: {
  zeilen: Standzeile[]
  kopf: string | null
  aufSprung: (ziel: Sprung) => void
}) {
  if (zeilen.length === 0) {
    return <p className="hinweis">{t('kachel.stand.ohne')}</p>
  }

  return (
    <div className="kachelstand">
      {kopf && <p className="standkopf">{kopf}</p>}
      <ul>
        {zeilen.map((z) => (
          <li key={z.id}>
            <button
              type="button"
              className={`standzeile ${z.zustand}`}
              onClick={() => z.ziel && aufSprung(z.ziel)}
              disabled={!z.ziel}
            >
              <Zustandssymbol art={z.zustand} />
              <span className="name">{z.name}</span>
              <span className="menge">{z.menge}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}

/* --- Die erste Kachel des Prüfers (R353) ------------------------------------ */

/**
 * Die Abschlüsse mit ihrem Zustand und ihrem Prüfcode.
 *
 * **Sie ist die erste Kachel des Prüfers und nicht seine Arbeitsliste**
 * (7.1 Abschnitt 6, R353): Für ihn gibt es keinen Arbeitsvorrat, und ein
 * dauerhaft leerer Kasten wäre die Behauptung, es gäbe einen.
 *
 * Der **Prüfcode steht in der Zeile** und nicht hinter einem Klick (R104/R110):
 * Wer prüft, vergleicht ihn mit dem Ausdruck, den er auf dem Tisch hat — und
 * das tut er nicht, wenn er ihn erst aufklappen muss.
 */
export function Abschlusstafel({
  abschluesse,
  aufSprung,
}: {
  abschluesse: Abschluss[] | null
  aufSprung: (ziel: Sprung) => void
}) {
  if (abschluesse === null) {
    return <p className="hinweis">{t('kachel.laedt')}</p>
  }

  if (abschluesse.length === 0) {
    return <p className="hinweis">{t('abschlusstafel.leer')}</p>
  }

  // Der jüngste zuerst: Geprüft wird der laufende Abschluss, nicht der von
  // vorletztem Jahr.
  const sortiert = [...abschluesse].sort((a, b) => b.periode - a.periode)

  return (
    <div className="abschlusstafel">
      <h1>{t('abschlusstafel.titel')}</h1>

      <ul>
        {sortiert.map((a) => {
          const fertig = a.status !== 'offen' && a.status !== 'in_arbeit'

          return (
            <li key={a.schluessel}>
              <button
                type="button"
                className={`abschlusszeile ${fertig ? 'fertig' : 'offen'}`}
                onClick={() => aufSprung({ bereich: 'nachweis', unterpunkt: 'aenderungsnachweis' })}
              >
                <Zustandssymbol art={fertig ? 'fertig' : 'offen'} />
                <span className="wann">{periodenname(a.periode)}</span>
                {/* Kreis und Bewertungsebene beim Namen — „KRS-DE · LDG-HGB" ist keine Auskunft (R838). */}
                <span className="wer">
                  {kreisname(a.kreis)} · {bewertungsebenenname(a.ledger)}
                </span>
                <span className="status">{abschlussstatus(a.status)}</span>
                {/* **Kein Prüfcode auf der Kachel** (7.2 Abschnitt 37, R1147).
                    Dass noch nicht gerechnet ist, bleibt eine Auskunft (R145). */}
                {!a.ergebnisHash && <span className="pruefcode">{t('abschlusstafel.ohnecode')}</span>}
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

/* --- Woraus ein Stand entsteht ---------------------------------------------- */

/**
 * Die Konsolidierungsschritte des Laufs.
 *
 * **Eingesammelt, nicht erfunden** (R140): Es sind dieselben Vorgänge, die der
 * Unterpunkt *Konsolidierungsschritte* zeigt — hier nur auf Name, Menge und
 * Zustand zusammengezogen.
 */
export function schrittzeilen(vorgaenge: readonly Vorgangskopf[]): Standzeile[] {
  return [...vorgaenge]
    .sort((a, b) => a.reihenfolge - b.reihenfolge)
    .map((v) => ({
      id: String(v.id),
      name: aktuelleSprache() === 'de' ? v.nameDe : v.nameEn || v.nameDe,
      // Gezählt werden die **Buchungen** des Schritts, nicht Schritte: Die
      // Währungsumrechnung bucht im Demobestand null Mal, und „0 Schritte" wäre
      // die Auskunft, dass es den Schritt nicht gibt — den gibt es aber.
      menge: t(v.buchungen === 1 ? 'kachel.stand.buchung' : 'kachel.stand.buchungen', {
        anzahl: anzahlwort(v.buchungen),
      }),
      zustand: 'fertig' as Zustandsart,
      ziel: { bereich: 'abschluss', unterpunkt: 'konzernabschluesse' },
    }))
}

/**
 * Der Lieferstand je Gesellschaft (J2) — aus den Importläufen.
 *
 * **Was hier fehlt, ist benannt** (7.5 Abschnitt 7): „Welche Tochter hat nach
 * dem Periodenschluss geliefert" braucht einen eigenen Endpunkt aus Bereich 5.
 * Was da ist, ist die Frage davor — **hat sie überhaupt geliefert?** —, und die
 * beantworten die Importläufe der Periode.
 */
export function lieferzeilen(laeufe: readonly Importlaufkopf[], periode: number): Standzeile[] {
  const dieser = laeufe.filter((l) => l.periode === periode)
  const jeEinheit = new Map<string, Importlaufkopf[]>()

  for (const l of dieser) {
    jeEinheit.set(l.einheit, [...(jeEinheit.get(l.einheit) ?? []), l])
  }

  // Der Name der Gesellschaft, nicht ihr Schlüssel (R792) — bis zum
  // 2026-10-06 stand hier „EH-000005-57". Sortiert wird nach dem, was dasteht.
  return [...jeEinheit.entries()]
    .map(([einheit, ihre]) => ({ einheit, name: gesellschaftsname(einheit), ihre }))
    .sort((a, b) => a.name.localeCompare(b.name, aktuelleSprache()))
    .map(({ einheit, name, ihre }) => {
      const uebernommen = ihre.reduce((s, l) => s + l.zeilenUebernommen, 0)
      const abgewiesen = ihre.reduce((s, l) => s + l.zeilenAbgewiesen, 0)

      return {
        id: einheit,
        name,
        menge: t('kachel.stand.geliefert', {
          geliefert: anzahlwort(uebernommen),
          gesamt: anzahlwort(uebernommen + abgewiesen),
        }),
        zustand: (abgewiesen > 0 ? 'offen' : 'fertig') as Zustandsart,
        ziel: { bereich: 'datenimport', unterpunkt: 'lieferungen' },
      }
    })
}

/** Der Systemzustand — die Zusage aus 4.1 gegen die Messung. */
export function systemzeilen(
  messung: Messwerte | null,
  datenquellen: Datenquelle[] | null = null,
): Standzeile[] {
  // Die Datenquellen mit offenen Zielfeldern (R523): gelb, kein Blocker —
  // dieselbe Auskunft wie unter Betrieb · Systemzustand, als Zeile der Kachel.
  const quellzeilen: Standzeile[] = []
  if (datenquellen) {
    const aktive = datenquellen.filter((q) => q.istAktiv)
    const offene = aktive.filter((q) => (q.stand?.offen ?? 0) > 0).length
    quellzeilen.push({
      id: 'datenquellen',
      name: t('kachel.stand.datenquellen'),
      menge: t('kachel.stand.datenquellen.offen', { anzahl: anzahlwort(offene), gesamt: anzahlwort(aktive.length) }),
      zustand: offene === 0 ? 'fertig' : 'offen',
      ziel: { bereich: 'datenimport', unterpunkt: 'quellen' },
    })
  }

  if (!messung) {
    return quellzeilen
  }

  const haelt = messung.p95Ms <= messung.budgetMs

  return [
    ...quellzeilen,
    {
      id: 'antwortzeit',
      name: t('kachel.groesse.antwortzeit'),
      menge: t('kachel.einheit.ms', { ms: anzahlwort(messung.p95Ms) }),
      zustand: haelt ? 'fertig' : 'offen',
      ziel: { bereich: 'betrieb', unterpunkt: 'antwortzeiten' },
    },
    {
      id: 'budget',
      name: t('kachel.stand.zusage', { budget: anzahlwort(messung.budgetMs) }),
      // „35" allein wäre keine Auskunft — die Menge sagt, wovon (R140).
      menge: t('kachel.stand.ueberbudget', {
        anzahl: anzahlwort(messung.ueberBudget),
        gesamt: anzahlwort(messung.anzahl),
      }),
      zustand: messung.ueberBudget === 0 ? 'fertig' : 'offen',
      ziel: { bereich: 'betrieb', unterpunkt: 'systemzustand' },
    },
    {
      id: 'mittel',
      name: t('messung.mittel'),
      menge: t('kachel.einheit.ms', { ms: anzahlwort(messung.mittlereMs) }),
      zustand: 'fertig',
      ziel: { bereich: 'betrieb', unterpunkt: 'antwortzeiten' },
    },
  ]
}

/** Die Zahl unter dem Kopf einer Verlaufskachel — für die Kopfzeile des Rahmens. */
export function verlaufskopf(punkte: readonly Verlaufspunkt[]): string {
  const letzter = punkte[punkte.length - 1]
  return letzter ? betrag(letzter.wert) : ''
}
